'use client';
import ClickableTooltip from '@/components/Styled/ClickableTooltip';
import { Listbox, Transition } from '@headlessui/react';
import Typography from '@ui/Typography';
import { cx } from 'cva';
import React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { MdOutlineCheck } from 'react-icons/md';

export default function Select(props) {
  const {
    label,
    placeholder,
    required,
    className,
    error,
    options,
    onChange,
    value,
    tooltip = '',
    tooltipProps = {},
    ...rest
  } = props;
  const [selectedOption, setSelectedOption] = useState(value || null);
  const hasError = !!error;

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  const handleSelect = (opt) => {
    setSelectedOption(opt);
    if (onChange) {
      onChange(opt);
    }
  };

  return (
    <Listbox value={selectedOption} onChange={handleSelect}>
      <div className="relative">
        <div className="flex flex-col gap-0.5">
          {label && (
            <div className="flex items-center justify-start gap-0.5">
              <Typography
                variant="h5"
                className="uppercase"
                component="label"
                htmlFor={label}>
                {label}
              </Typography>
              {required && (
                <Typography variant="h5" component="span" color="error">
                  *
                </Typography>
              )}
              {tooltip && (
                <ClickableTooltip message={tooltip} {...tooltipProps} />
              )}
            </div>
          )}
          <Listbox.Button
            className={cx(
              'focus:shadow-outline relative w-full cursor-pointer rounded-lg bg-primary-light-1 px-6 py-3 text-left focus:outline-none',
              hasError && 'border border-error',
            )}>
            {selectedOption?.element ? (
              selectedOption.element
            ) : (
              <Typography
                variant="h5"
                component="span"
                className="mr-4 truncate">
                {selectedOption ? selectedOption.label : placeholder || label}
              </Typography>
            )}

            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-6">
              <HiChevronDown
                className="h-5 w-5 text-text-secondary"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          {hasError && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <Listbox.Options className="sm:text-sm absolute z-100 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {options.map((opt) => (
              <Listbox.Option
                key={opt.id}
                className={({ active }) =>
                  cx(
                    `relative cursor-pointer select-none py-2 pl-10 pr-4`,
                    active && 'bg-primary-light-1',
                  )
                }
                value={opt}>
                {({ selected }) => (
                  <>
                    <Typography
                      variant="h5"
                      component="span"
                      color={selected ? 'textPrimary' : 'textSecondary'}
                      className="truncate">
                      {opt.label}
                    </Typography>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary">
                        <MdOutlineCheck
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
